/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

/**
 * @module bulb-audio/bulbaudioediting
 */

import {
  Plugin,
  Widget,
  toWidget,
  ViewElement,
  type ModelElement,
  UpcastConversionApi,
  DowncastConversionApi,
} from 'ckeditor5';

import { InsertBulbAudioCommand } from './bulbaudiocommand';
import { getBlockVersion, getJWPlayer } from '../utils';

/**
 * The bulb audio editing infrastructure, this will create the block using the converters.
 *
 * @extends Plugin
 */
export default class BulbAudioEditing extends Plugin {

  /**
   * @inheritDoc
   */
  public static get requires() {
    return [Widget] as const;
  }

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbAudioEditing' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    this.defineSchema();
    this.defineConverters();

    this.editor.commands.add('insertAudio', new InsertBulbAudioCommand(this.editor));
  }

  /**
   * Method that defines the schema four our audio block.
 *
   * @private
   */
  private defineSchema(): void {
    const { schema } = this.editor.model;

    schema.register('audio', {
      isObject: true,
      isBlock: true,
      allowWhere: '$block',
      allowAttributes: ['bulb-block-id', 'blockAlignment', 'width', 'bulb-block-version'],
    });
  }

  /**
   * Method that creates and initializes the downcast ( editing and data ) and
   * upcast converters for our audio block.
   *
   * @private
   */
  private defineConverters(): void {
    const { conversion } = this.editor;

    conversion.for('upcast').elementToElement({
      view: {
        name: 'figure',
        classes: 'audio',
      },
      model: (
        viewElement: ViewElement,
        { writer: modelWriter }: UpcastConversionApi,
      ): ModelElement => modelWriter.createElement('audio', {
        'bulb-block-id': viewElement.getAttribute('bulb-block-id'),
        'bulb-block-version': getBlockVersion(CKEBulbData, viewElement),
      }),
    });

    conversion.for('dataDowncast').elementToElement({
      model: 'audio',
      view: (
        modelElement: ModelElement,
        { writer: viewWriter }: DowncastConversionApi,
      ) => viewWriter.createContainerElement('figure', {
        class: 'audio',
        'bulb-block-id': modelElement.getAttribute('bulb-block-id'),
        'bulb-block-version': getBlockVersion(CKEBulbData, modelElement),
      }),
    });

    conversion.for('editingDowncast').elementToElement({
      model: 'audio',
      view: (modelElement: ModelElement, { writer: viewWriter }: DowncastConversionApi) => {
        const contentBlock = CKEBulbData.page.unit.contentBlocks[modelElement.getAttribute('bulb-block-id') as any];
        const { audio } = contentBlock;

        const assetUrls = audio.transcodedAssets.map((x) => x.url);
        const assetBitrates = audio.transcodedAssets.map((x) => x.bitrate);

        const jwplayer = getJWPlayer();
        const playerId = modelElement.getAttribute('bulb-block-id');

        const jwPlayerWrapper = viewWriter.createUIElement('div', {
          class: 'bulb_jwplayer_wrapper no-editor-placeholder',
        }, function (domDocument) {
          const domElement = this.toDomElement(domDocument);
          domElement.innerHTML = `<div id="${playerId}"></div>`;

          const audioArray = BulbAudioEditing.createJWPlayerAudioArray(assetUrls, assetBitrates);
          // Do a timeout here without a second parameter so that this function runs
          // after the queue. This will ensure that the block is on the page before
          // running this block of code.
          setTimeout(() => {
            const jwPlayerInstance = jwplayer(playerId);
            jwPlayerInstance.setup({
              sources: audioArray,
              height: 40, // Audio Mode
            });
          });

          return domElement;
        });

        const figure = viewWriter.createContainerElement('figure', {
          class: 'audio',
          'bulb-block-id': modelElement.getAttribute('bulb-block-id'),
          'bulb-block-version': modelElement.getAttribute('bulb-block-version') || 0,
        });

        viewWriter.insert(viewWriter.createPositionAt(figure, 'end'), jwPlayerWrapper);

        viewWriter.setCustomProperty('audio', true, figure);
        return toWidget(figure, viewWriter, { label: this.editor.t('editor_audio_block'), hasSelectionHandle: true });
      },
    });
  }

  /**
   * Method that creates and populates the audio array of transcode audio assets to initialize JWPlayer with.
   *
   * @param url Array of string urls to the transcode audio assets that are hosted on s3.
   * @param bitRate Array of the associated bitrates for the transcode audio assets.
   * @returns {[]} audioArray with all the necessary information to initialize JWPlayer with.
   *
   * @private
   */
  private static createJWPlayerAudioArray(
    url: string | string[],
    bitRate: string | number[],
  ): { file: string; label: number }[] {
    // JWPlayer accepts an array of data featuring a url and a height to display in the visual options.
    // We have these, but need to create them.
    let urlArray: string[] = [];
    let bitRateArray: number[] = [];
    if (Array.isArray(url)) {
      urlArray = url;
    } else if (typeof url === 'string') {
      urlArray = url.split(',');
    }

    if (Array.isArray(bitRate)) {
      bitRateArray = bitRate;
    } else if (typeof bitRate === 'string') {
      bitRateArray = bitRate.split(',').map(Number);
    }
    if (typeof url === 'string') {
      urlArray = url.split(',');
    }
    if (typeof bitRate === 'string') {
      bitRateArray = bitRate.split(',').map(Number);
    }

    const audioArray: { file: string; label: number }[] = [];

    for (let i = 0; i < urlArray.length; i++) {
      const audioObject = { file: urlArray[i], label: bitRateArray[i] };
      audioArray.push(audioObject);
    }

    return audioArray;
  }
}
